import { motion, AnimatePresence } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { Compass, MapPin, Calendar, ArrowRight, Anchor, Globe, Map } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { useUser } from '../context/UserContext';
import { useState } from 'react';
import UnifiedBookingCalendar from './UnifiedBookingCalendar';

export default function ExpeditionsHub() {
  const { assets, resolveMediaUrl, content } = useBrand();

  const [selectedExpedition, setSelectedExpedition] = useState<'day-trip' | 'crossing' | 'international' | undefined>(undefined);

  const scrollToCalendar = () => {
    const el = document.getElementById('expedition-calendar');
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleServiceClick = (serviceType: 'day-trip' | 'crossing' | 'international') => {
    setSelectedExpedition(serviceType);
    scrollToCalendar();
  };

  return (
    <div id="expedition-hub" className="bg-ows-dark min-h-screen">
      <Helmet>
        <title>Expeditions | Local & International | OWS</title>
        <meta name="description" content="Join guided open water expeditions with Open Water Syndicate. From local Lake Ontario routes to international oceanic adventures." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20 md:py-0">
        <div className="absolute inset-0 z-0">
          {assets.nightRaidHeroVideo ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover opacity-60 grayscale-[0.2] contrast-[1.1]"
              src={resolveMediaUrl(assets.nightRaidHeroVideo)}
            />
          ) : assets.nightRaidHeroImage ? (
            <img
              src={resolveMediaUrl(assets.nightRaidHeroImage)}
              alt="Expeditions"
              className="w-full h-full object-cover opacity-60 grayscale-[0.2] contrast-[1.1]"
            />
          ) : (
            <div className="w-full h-full bg-ows-dark" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-ows-dark via-ows-dark/80 to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 mt-16">
          <div className="max-w-4xl">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl md:text-7xl font-display font-light uppercase tracking-tight mb-8 leading-none text-white"
            >
              Beyond The <br /> Horizon Line.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-lg md:text-xl leading-relaxed mb-6 max-w-2xl font-serif italic"
            >
              The Syndicate leads specialized, small-batch expeditions designed to push boundaries. From local training runs on Lake Ontario to international open water crossings.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16"
            >
              <button onClick={() => handleServiceClick('day-trip')} className="text-left group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/10 hover:border-ows-teal border-b-2 transition-all flex flex-col justify-between h-full relative overflow-hidden block">
                <div className="absolute inset-0 bg-gradient-to-br from-ows-teal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 mb-8">
                  <MapPin className="h-8 w-8 text-white mb-6" />
                  <h4 className="text-2xl font-display uppercase tracking-widest text-ows-teal mb-4 group-hover:text-white transition-colors">Local Day <br /> Expeditions</h4>
                  <p className="text-white/70 font-sans text-sm font-light tracking-wide leading-relaxed group-hover:text-white transition-colors">
                    Intensive half or full-day coastal routes exploring Toronto's outer harbor and the Leslie Street Spit. Ideal for mastering endurance without setting camp.
                  </p>
                </div>
                <div className="relative z-10 flex items-center gap-3">
                  <span className="bg-ows-teal text-white w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-ows-teal transition-colors">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 group-hover:text-white transition-colors">View Availability</span>
                </div>
              </button>

              <button onClick={() => handleServiceClick('crossing')} className="text-left group bg-[#0D3A40]/40 backdrop-blur-md border border-ows-teal/30 p-8 rounded-2xl hover:bg-[#0D3A40]/80 hover:border-ows-teal border-b-2 hover:-translate-y-2 transition-all flex flex-col justify-between h-full relative overflow-hidden block shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-ows-orange/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 mb-8">
                  <Anchor className="h-8 w-8 text-ows-orange mb-6" />
                  <h4 className="text-2xl font-display uppercase tracking-widest text-ows-orange mb-4 group-hover:text-white transition-colors">Great Lakes</h4>
                  <p className="text-white/70 font-sans text-sm font-light tracking-wide leading-relaxed group-hover:text-white transition-colors">
                    Single-day unsupported crossings of Lake Ontario. We manage routing and safety protocols. You supply the endurance.
                  </p>
                </div>
                <div className="relative z-10 flex items-center gap-3">
                  <span className="bg-ows-orange text-white w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-ows-orange transition-colors">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 group-hover:text-white transition-colors">VIEW CROSSING SCHEDULE</span>
                </div>
              </button>

              <button onClick={() => handleServiceClick('international')} className="text-left group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/10 hover:border-ows-teal border-b-2 transition-all flex flex-col justify-between h-full relative overflow-hidden block">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 mb-8">
                  <Globe className="h-8 w-8 text-white mb-6" />
                  <h4 className="text-2xl font-display uppercase tracking-widest text-ows-teal mb-4 group-hover:text-white transition-colors">International <br /> Expeditions</h4>
                  <p className="text-white/70 font-sans text-sm font-light tracking-wide leading-relaxed group-hover:text-white transition-colors">
                    Invite-only expeditions in international waters. From the Gulf of California to the Nordic Fjords.
                  </p>
                </div>
                <div className="relative z-10 flex items-center gap-3">
                  <span className="bg-ows-teal text-white w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-ows-teal transition-colors">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 group-hover:text-white transition-colors">View Expedition Calendar</span>
                </div>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Unified Booking Calendar Section */}
      <section id="expedition-calendar" className="py-24 bg-ows-dark border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display uppercase tracking-widest text-white mb-4">Book an Expedition</h2>
            <p className="text-white/50 font-serif italic max-w-xl mx-auto">Select your expedition. Capacity is limited for safety..</p>
          </div>
          <UnifiedBookingCalendar theme="dark" type="expedition" />
        </div>
      </section>
    </div>
  );
}
